/**
 * Column definitions for the tickets list DataTable.
 * Mirrors legacy `modules/Ticket/utils/ticket-list-config.tsx` — mapped to
 * @vnetwork/ui primitives. Field names verified against live /tickets probe.
 */
import type { ColumnDef } from '@vnetwork/ui/data';
import { StatusPill } from '@vnetwork/ui/data';
import { Badge } from '@vnetwork/ui/primitives';
import { formatDateTime } from '@/lib/format';
import type { Ticket } from '@/api/tickets';
import {
  TICKET_STATUS_LABEL,
  TICKET_STATUS_OPENING,
  TICKET_STATUS_WORK_IN_PROGRESS,
  TICKET_STATUS_COMPLETED,
  TICKET_STATUS_CLOSED,
  TICKET_STATUS_ESCALATED,
} from '@/api/tickets-constants';
import { TableLink } from '@/components/table-link';
import { EntityLink } from '@/components/entity-link';

/* ── Status → tone map ───────────────────────────────────────────────────── */

const STATUS_TONE: Record<string, 'warning' | 'info' | 'success' | 'neutral' | 'danger'> = {
  [TICKET_STATUS_OPENING]: 'warning',
  [TICKET_STATUS_WORK_IN_PROGRESS]: 'info',
  [TICKET_STATUS_COMPLETED]: 'success',
  [TICKET_STATUS_CLOSED]: 'neutral',
  [TICKET_STATUS_ESCALATED]: 'danger',
};

// Legacy priority vocab: low/medium/high only. Neutral fallback for unknowns.
const PRIORITY_TONE: Record<string, 'neutral' | 'info' | 'warning'> = {
  low: 'neutral',
  medium: 'info',
  high: 'warning',
};

/* ── Shared cells ────────────────────────────────────────────────────────── */

/** Muted italic placeholder for unassigned assignee cells. */
function UnassignedCell() {
  return <span style={{ color: 'var(--vn-fg-subtle)', fontStyle: 'italic' }}>Unassigned</span>;
}

/* ── Column definitions ──────────────────────────────────────────────────── */

export const TICKET_COLUMNS: ColumnDef<Ticket, unknown>[] = [
  {
    id: 'ticket_id',
    header: 'ID',
    cell: ({ row }) => (
      <TableLink to={`/tickets/${row.original.ticket_id}`}>
        {row.original.ticket_id}
      </TableLink>
    ),
  },
  {
    id: 'employee_name',
    header: 'Creator',
    cell: ({ row }) => (
      <span>{row.original.employee_name || row.original.created_by_name || '—'}</span>
    ),
  },
  {
    id: 'ticket_status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.ticket_status;
      return (
        <StatusPill tone={STATUS_TONE[status] ?? 'neutral'}>
          {TICKET_STATUS_LABEL[status] ?? status}
        </StatusPill>
      );
    },
  },
  {
    id: 'ticket_priority',
    header: 'Priority',
    cell: ({ row }) => {
      const priority = row.original.ticket_priority;
      return (
        <Badge tone={PRIORITY_TONE[priority] ?? 'neutral'}>
          {priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : '—'}
        </Badge>
      );
    },
  },
  {
    id: 'ticket_requested_at',
    header: 'Requested At',
    cell: ({ row }) => (
      <span style={{ whiteSpace: 'nowrap' }}>
        {formatDateTime(row.original.ticket_requested_at) || '—'}
      </span>
    ),
  },
  {
    id: 'customer_name',
    header: 'Customer',
    cell: ({ row }) => {
      const { customer_id, customer_name, company_name } = row.original;
      return (
        <EntityLink
          to={`/customers/${customer_id}`}
          id={customer_id}
          name={company_name || customer_name}
        />
      );
    },
  },
  {
    id: 'ticket_type_group',
    header: 'Type Group',
    cell: ({ row }) => <span>{row.original.ticket_type_group || '—'}</span>,
  },
  {
    id: 'ticket_technical_assignees',
    header: 'Tech Assignees',
    cell: ({ row }) => {
      const names = (row.original.ticket_technical_assignee_names ?? [])
        .map((a) => a.employee_name)
        .join(', ');
      return names ? <span>{names}</span> : <UnassignedCell />;
    },
  },
  {
    id: 'ticket_cs_assignees',
    header: 'CS Assignees',
    cell: ({ row }) => {
      const names = (row.original.ticket_cs_assignee_names ?? [])
        .map((a) => a.employee_name)
        .join(', ');
      return names ? <span>{names}</span> : <UnassignedCell />;
    },
  },
  {
    id: 'ticket_sale_assignees',
    header: 'Sale Assignees',
    cell: ({ row }) => {
      const names = (row.original.ticket_sale_assignee_names ?? [])
        .map((a) => a.employee_name)
        .join(', ');
      return names ? <span>{names}</span> : <UnassignedCell />;
    },
  },
  {
    id: 'ticket_type_title',
    header: 'Ticket Type',
    cell: ({ row }) => <span>{row.original.ticket_type_title || '—'}</span>,
  },
  {
    id: 'product_group_name',
    header: 'Product Group',
    cell: ({ row }) => {
      const names = Array.isArray(row.original.product_group_name)
        ? row.original.product_group_name.join(', ')
        : (row.original.product_group_name ?? '—');
      return <span>{names || '—'}</span>;
    },
  },
  {
    id: 'ticket_estimated_completion_date',
    header: 'Estimated Completion',
    cell: ({ row }) => (
      <span style={{ whiteSpace: 'nowrap' }}>
        {formatDateTime(row.original.ticket_estimated_completion_date) || '—'}
      </span>
    ),
  },
  {
    id: 'created_at',
    header: 'Created',
    cell: ({ row }) => (
      <span style={{ whiteSpace: 'nowrap' }}>
        {formatDateTime(row.original.created_at) || '—'}
      </span>
    ),
  },
];

/** Columns hidden by default; user can toggle via Display options. */
export const TICKET_TOGGLEABLE_COLUMN_IDS = [
  'ticket_cs_assignees',
  'ticket_sale_assignees',
  'ticket_type_title',
  'product_group_name',
  'ticket_estimated_completion_date',
];
